/*
 * ServerReconnect watchdog — pure decision logic.
 *
 * app.jsx's <ServerReconnect/> polls /api/health every few seconds and shows a
 * "connection lost" banner while the server is unreachable, then a "server is
 * back — refresh" banner once it recovers or the process restarts under us
 * (uptime goes backwards). This module holds the state machine so it can be
 * unit tested in Node (test/reconnect-logic.test.js) while app.jsx imports it
 * for the browser bundle.
 *
 * Two properties matter and were both wrong in the old inline version:
 *   1. Debounce. A single failed ping is NOT an outage — health checks blip
 *      under host load. Only DOWN_THRESHOLD consecutive failures raise the
 *      "connection lost" banner.
 *   2. Surface-once recovery. When the server comes back (or restarts under us)
 *      the "refresh" banner is returned exactly ONCE. The old code left an
 *      internal `wasDown` flag latched forever, so every later healthy ping
 *      re-raised the banner every tick and the user could never dismiss it.
 *
 * State  : { fails, lastUp, wasDown } — carry the returned `state` into the next
 *          tick. Start from { fails: 0, lastUp: null, wasDown: false }.
 * Sample : { ok, up } — ok = the ping succeeded, up = uptimeSecs (number|null).
 * Returns: { state, down, ready } — down/ready drive the two banners. `down` is
 *          the live outage state each tick; `ready` is an edge (true only on the
 *          tick recovery is detected) that the component latches until dismissed.
 */

export const DOWN_THRESHOLD = 2;

export function reconnectTick(state, sample, downThreshold = DOWN_THRESHOLD) {
  const s = {
    fails: (state && state.fails) | 0,
    lastUp: state && state.lastUp != null ? state.lastUp : null,
    wasDown: !!(state && state.wasDown),
  };

  // Failed ping: count it. Only sustained failure is a real outage.
  if (!sample || sample.ok !== true) {
    s.fails += 1;
    if (s.fails >= downThreshold) {
      s.wasDown = true;
      return { state: s, down: true, ready: false };
    }
    // Not enough consecutive misses yet — hold the current visible state.
    return { state: s, down: false, ready: false };
  }

  // Successful ping: clear the miss streak.
  s.fails = 0;
  const up = typeof sample.up === "number" ? sample.up : null;
  const restarted = s.lastUp != null && up != null && up < s.lastUp;
  const recovered = s.wasDown;
  if (recovered || restarted) {
    // Surface the refresh banner ONCE, then clear the latch and re-baseline so
    // the next healthy ping does not re-raise it.
    s.wasDown = false;
    s.lastUp = up;
    return { state: s, down: false, ready: true };
  }
  s.lastUp = up;
  return { state: s, down: false, ready: false };
}
